import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ApiService } from '../../../shared/services/api.service';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { CollaborationDataService } from '../../services/collaboration-data.service';
import {
  GetDataConnectionListQuery,
  GetDataConnectionListQueryVariables,
  ConnectionStatus,
  SyncStatus,
  Action,
  collaborationUpdateInput,
  OrgType,
  UpdateCollaborationDatasetMutation,
  UpdateCollaborationDatasetMutationVariables,
} from '../../../../API';
import { GetDataConnectionList } from '../../../../graphql/queries';
import { DataConnectionObj, childMetaDataObj } from '../../../data-connection/models/data-connection';
import { CollaborationItem, Dataset } from '../../models/collaboration.model';
import { ActivatedRoute, Router } from '@angular/router';
import { UpdateCollaborationDataset } from '../../../../graphql/mutations';
import { AppService } from '../../../shared/services/app.service';

@Component({
  selector: 'app-collaboration-dataset',
  templateUrl: './collaboration-dataset.component.html',
  styleUrl: './collaboration-dataset.component.scss',
})
export class CollaborationDatasetComponent implements OnInit {
  @Output() nextStep = new EventEmitter<void>();
  @Output() previousStep = new EventEmitter<void>();
  isLoading: boolean = false;
  selectedDatasets: Dataset[] = [];
  availableDatasets: Dataset[] = [];
  searchValue: string = '';
  filteredDatasets: Dataset[] = [];
  allChecked: boolean = false;
  indeterminate: boolean = false;
  isSponsorOrg: boolean = false;
  isAddRemoveDataset: boolean = false;
  statusFilter: { text: string; value: string }[] = [
    { text: 'Active', value: SyncStatus.ACTIVE },
    { text: 'Pending', value: SyncStatus.PENDING },
  ];

  constructor(
    private nzMessageService: NzMessageService,
    private apiService: ApiService,
    private userManagementService: UserManagementService,
    private collaborationDataService: CollaborationDataService,
    private activateRoute: ActivatedRoute,
    private router: Router,
    private appService: AppService,
  ) {}

  ngOnInit(): void {
    this.isAddRemoveDataset = this.router.url?.includes('/dataset');
    this.isSponsorOrg = this.userManagementService.getOrganizationType() === OrgType.SPONSOR;
    this.selectedDatasets = [...this.collaborationDataService.selectedDatasets];
    this.loadDataConnections();
    this.collaborationDataService.errorMessage = '';
  }

  loadDataConnections(): void {
    this.isLoading = true;
    this.apiService
      .query<GetDataConnectionListQuery, GetDataConnectionListQueryVariables>(GetDataConnectionList, {
        orgType: this.userManagementService.getOrgType(),
      })
      .then((response) => {
        const parentTableData = response.data?.GetDataConnectionList.dataConnectionList as any;
        const childTableData = JSON.parse(response.data?.GetDataConnectionList.metadataList as any);

        const dataConnections = parentTableData
          .filter((item: DataConnectionObj) => item.connectionStatus === ConnectionStatus.SUCCESS)
          .map((item: DataConnectionObj) => {
            const childMetaData = childTableData.find(
              (childItem: childMetaDataObj) => childItem.dataConnectionId === item.connectionId,
            );
            item.metadataList = childMetaData && JSON.parse(childMetaData.metadata);
            return item;
          });

        this.availableDatasets = this.transformDataConnectionsToDatasets(dataConnections);
        this.filteredDatasets = [...this.availableDatasets];
        // Set to previously selected data as selected; for back button from step3
        this.filteredDatasets.forEach((dataset: Dataset) => {
          dataset.isSelected = this.selectedDatasets.some(
            (preDataset: Dataset) =>
              preDataset.dataConnectionId == dataset.dataConnectionId && preDataset.tableName == dataset.tableName,
          );
        });
        this.updateCheckboxState();
        this.updateValidationState();
      })
      .catch((error) => {
        this.nzMessageService.error(`Error Fetching Data Connections: ${error?.message}`);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  transformDataConnectionsToDatasets(connections: DataConnectionObj[]): Dataset[] {
    return connections
      .flatMap((connection) => {
        if (connection.metadataList && connection.metadataList.length > 0) {
          const { connectionId, connectionName } = connection;
          return connection.metadataList.map((metadata) => {
            const { table_name, database_name, sync_status } = metadata ?? {};
            if (sync_status === SyncStatus.INACTIVE) return;
            const dataset: Dataset = {
              tableName: table_name as string,
              schemaName: database_name as string,
              dataConnectionId: connectionId,
              dataConnectionName: connectionName,
              status: sync_status as string,
              isSelected: false,
            };
            return dataset;
          });
        } else return [];
      })
      .filter((dataset) => dataset !== undefined) as Dataset[];
  }

  getDisplayStatus(syncStatus: any): string {
    switch (syncStatus) {
      case SyncStatus.ACTIVE:
        return 'Active';
      case SyncStatus.PENDING:
        return 'Pending';
      default:
        return 'Inactive';
    }
  }

  onSearch(): void {
    if (!this.searchValue.trim()) {
      this.filteredDatasets = [...this.availableDatasets];
    } else {
      const searchTerm = this.searchValue.toLowerCase();
      this.filteredDatasets = this.availableDatasets.filter(
        (dataset) =>
          dataset.dataConnectionName?.toLowerCase().includes(searchTerm) ||
          dataset.tableName?.toLowerCase().includes(searchTerm) ||
          dataset.schemaName?.toLowerCase().includes(searchTerm),
      );
    }
    this.updateCheckboxState();
  }

  onDatasetSelect(dataset: Dataset, checked: any): void {
    if (dataset.status === SyncStatus.PENDING) {
      return;
    }

    dataset.isSelected = checked;

    const selectedDataset: Dataset = {
      dataConnectionId: dataset.dataConnectionId,
      dataConnectionName: dataset.dataConnectionName,
      tableName: dataset.tableName,
      schemaName: dataset.schemaName,
      status: dataset.status,
      isSelected: checked,
    };
    this.updateDataset(selectedDataset, checked);
    this.updateCheckboxState();
    this.updateValidationState();
  }

  onBack(): void {
    this.collaborationDataService.currentStep = 0;
    this.previousStep.emit();
  }

  onCancel(): void {
    // this method will be used only when user add/remove datasets
    this.router.navigate(['../list'], { relativeTo: this.activateRoute });
  }

  onSaveAsDraft(): void {
    this.selectedDatasets = this.availableDatasets.filter((dataset) => dataset.isSelected);
    this.collaborationDataService.selectedDatasets = this.selectedDatasets;
  }

  onNext(): void {
    this.selectedDatasets = this.availableDatasets.filter((dataset) => dataset.isSelected);

    if (this.selectedDatasets.length === 0) {
      this.nzMessageService.warning('Please select at least one dataset to continue');
      return;
    }

    this.collaborationDataService.currentStep = 2;
    this.nextStep.emit();
  }

  trackByDataset(index: number, dataset: any): string {
    return `${dataset.dataConnectionId}-${dataset.tableName}`;
  }

  getStatusColor(status: any): string | undefined {
    switch (status) {
      case SyncStatus.ACTIVE:
      case ConnectionStatus.ACTIVE:
      case ConnectionStatus.SUCCESS: {
        return 'success';
      }
      case ConnectionStatus.PENDING:
      case SyncStatus.PENDING: {
        return 'warning';
      }
      case ConnectionStatus.NOT_VERIFIED:
      case ConnectionStatus.INACTIVE:
      case ConnectionStatus.FAIL: {
        return 'error';
      }
    }
    return;
  }

  getActiveDatasets(): Dataset[] {
    return this.filteredDatasets.filter((dataset) => dataset.status === SyncStatus.ACTIVE);
  }

  updateAllChecked(): void {
    const activeDatasets = this.getActiveDatasets();

    activeDatasets.forEach((dataset) => {
      dataset.isSelected = this.allChecked;
      this.updateDataset(dataset, this.allChecked);
    });

    this.updateCheckboxState();
    this.updateValidationState();
  }

  updateCheckboxState(): void {
    const activeDatasets = this.getActiveDatasets();
    const selectedActiveCount = activeDatasets.filter((dataset) => dataset.isSelected).length;

    this.allChecked = selectedActiveCount === activeDatasets.length && activeDatasets.length > 0;
    this.indeterminate = selectedActiveCount > 0 && selectedActiveCount < activeDatasets.length;
  }

  updateDataset(dataset: Dataset, checked: boolean) {
    if (checked) {
      const existingIndex = this.selectedDatasets.findIndex(
        (d) => d.dataConnectionId === dataset.dataConnectionId && d.tableName === dataset.tableName,
      );
      if (existingIndex === -1) {
        this.selectedDatasets.push(dataset);
        this.collaborationDataService.addDataset(dataset);
      }
    } else {
      this.selectedDatasets = this.selectedDatasets.filter(
        (d) => d.dataConnectionId !== dataset.dataConnectionId || d.tableName !== dataset.tableName,
      );
      this.collaborationDataService.removeDataset(dataset);
    }
  }

  public addRemoveDataset(): void {
    this.isLoading = true;
    const collaborationData = this.collaborationDataService.getCompleteCollaborationData();
    const updatedDataset =
      collaborationData.ownerDatasets?.map((dataset: any) => ({
        dataConnectionId: dataset.dataConnectionId,
        dataConnectionName: dataset.dataConnectionName,
        schemaName: dataset.schemaName,
        tableName: dataset.tableName,
      })) || [];
    const updateInput: collaborationUpdateInput = {
      action: Action.UPDATE_DATASET,
      collaborationId: collaborationData.collaborationId as string,
      lastModifiedBy: this.userManagementService.getEmail(),
      orgType: collaborationData.orgType as string,
    };

    if (this.isSponsorOrg) {
      updateInput['partnerDatasets'] = updatedDataset;
    } else {
      updateInput['ownerDatasets'] = updatedDataset;
    }

    this.apiService
      .query<UpdateCollaborationDatasetMutation, UpdateCollaborationDatasetMutationVariables>(
        UpdateCollaborationDataset,
        {
          collaborationRequest: updateInput,
        },
      )
      .then((response) => {
        if (response.data?.UpdateCollaborationDataset?.success) {
          this.nzMessageService.success(
            response.data?.UpdateCollaborationDataset?.message || 'Collaboration dataset saved successfully!',
          );
          this.appService.refreshEngagementList();
          this.router.navigate(['../list'], { relativeTo: this.activateRoute });
        } else {
          this.nzMessageService.error(
            response.data?.UpdateCollaborationDataset?.message || 'Failed to save collaboration dataset',
          );
        }
      })
      .catch((error) => {
        this.nzMessageService.error(error.message || 'Failed to save collaboration dataset');
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  statusFilterFn = (statusList: string[], collaboration: CollaborationItem): boolean => {
    return statusList.some((status) => collaboration.status === status);
  };

  statusSortFn = (a: CollaborationItem, b: CollaborationItem): any => {
    return (a.status || '').localeCompare(b.status || '');
  };

  isValid(): boolean {
    return this.selectedDatasets.length > 0;
  }

  private updateValidationState(): void {
    this.collaborationDataService.isStep2Valid = this.isValid();
  }

  reset(): void {
    const original = this.collaborationDataService.originalCollaborationData;
    const originalDatasets = original?.ownerDatasets || [];

    this.selectedDatasets = [...originalDatasets];
    this.filteredDatasets.forEach((dataset) => {
      dataset.isSelected = originalDatasets.some(
        (origDataset: any) =>
          origDataset.dataConnectionId === dataset.dataConnectionId && origDataset.tableName === dataset.tableName,
      );
    });
    this.collaborationDataService.selectedDatasets = [...originalDatasets];
    this.searchValue = '';
    this.updateCheckboxState();
  }
}
